import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-4" };
const _hoisted_2 = { class: "text-base font-bold leading-5 text-(--colors-text-black-default)" };
const _hoisted_3 = ["aria-label"];
const _hoisted_4 = ["data-testid", "aria-checked", "aria-disabled", "tabindex", "aria-controls", "onClick"];
const _hoisted_5 = { class: "flex flex-col text-sm leading-5 text-(--colors-text-body)" };
const _hoisted_6 = { class: "font-semibold" };
const _hoisted_7 = {
    "data-testid": "filter-sharing-announcement",
    "aria-live": "polite",
    class: "sr-only"
};
const _hoisted_8 = ["id"];
const _hoisted_9 = {
    key: 0,
    "data-testid": "filter-sharing-directory-unavailable",
    role: "alert",
    class: "text-sm leading-5 text-(--text-text-error-strong)"
};
const _hoisted_10 = {
    key: 1,
    "data-testid": "filter-sharing-directory-rate-limited",
    role: "alert",
    class: "text-sm leading-5 text-(--text-text-error-strong)"
};
const _hoisted_11 = {
    key: 2,
    "data-testid": "filter-sharing-directory-error",
    role: "alert",
    class: "text-sm leading-5 text-(--text-text-error-strong)"
};
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { AccessSettings } from "@eudonet/opal";
import { useUserDirectorySearch } from "@/shared/composables/useUserDirectorySearch";
import { buildAccessSettingsLabels } from "@/shared/composables/accessSettingsLabels";
import { cloneSharing, DEFAULT_SHARED_ACCESS } from "@/shared/utils/filterSharing";
const cardBase = "flex h-[68px] items-center gap-3.5 rounded-xl border p-3.5";
const cardSelected = "border-(--colors-border-hover) bg-(--colors-background-hover)";
const cardIdle = "border-(--colors-border-base-medium) bg-(--colors-background-white)";
export default /*@__PURE__*/ _defineComponent({
    __name: 'FilterSharingFields',
    props: {
        modelValue: {},
        knownUsers: { default: () => [] },
        disabled: { type: Boolean, default: false }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const labels = computed(() => buildAccessSettingsLabels(t));
        const { options, loading, unavailable, rateLimited, failed, search } = useUserDirectorySearch();
        const destinations = ["personal", "shared"];
        const isShared = computed(() => props.modelValue.destination === "shared");
        const sharedBlockId = useId();
        // Not immediate: a modal opening on « Filtres partagés » shows the block with the dialog, only a change reveals it.
        const announcement = ref("");
        watch(isShared, (shared) => {
            announcement.value = shared ? t("advancedFilterLibrary.access.revealed") : "";
        });
        // AccessSettings keeps its own seen-people cache, lost when « Mes filtres » unmounts it: without this one, a person
        // picked from an earlier search comes back as a raw id once « Filtres partagés » is chosen again.
        const seenUsers = ref(new Map());
        watch(() => [options.value, props.knownUsers], ([found, known]) => {
            for (const user of [...found, ...known])
                seenUsers.value.set(user.id, user);
        }, { immediate: true });
        const knownUsersForSettings = computed(() => [...seenUsers.value.values()]);
        function select(destination) {
            if (props.disabled || destination === props.modelValue.destination)
                return;
            const { organization, users } = props.modelValue.access;
            const isEmpty = organization === null && users.length === 0;
            // Always a copy: DEFAULT_SHARED_ACCESS is a shared constant, and the prop's access is
            // the parent's (reactive) object.
            const source = destination === "shared" && isEmpty ? DEFAULT_SHARED_ACCESS : props.modelValue.access;
            emit("update:modelValue", cloneSharing({ destination, access: source }));
        }
        function onAccessChange(access) {
            emit("update:modelValue", { destination: props.modelValue.destination, access });
        }
        function onKeydown(event) {
            if (props.disabled)
                return;
            const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
            const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
            if (!forward && !backward)
                return;
            event.preventDefault();
            const current = destinations.indexOf(props.modelValue.destination);
            const next = destinations[(current + (forward ? 1 : destinations.length - 1)) % destinations.length];
            select(next);
            const group = event.currentTarget;
            group.querySelector(`[data-testid="save-filter-destination-${next}"]`)?.focus();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("advancedFilterLibrary.save.destinationLabel")), 1 /* TEXT */),
                _createElementVNode("div", {
                    role: "radiogroup",
                    "aria-label": _unref(t)('advancedFilterLibrary.save.destinationLabel'),
                    class: "grid grid-cols-2 gap-5",
                    onKeydown: onKeydown
                }, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(destinations, (destination) => {
                        return _createElementVNode("div", {
                            key: destination,
                            "data-testid": `save-filter-destination-${destination}`,
                            role: "radio",
                            "aria-checked": props.modelValue.destination === destination,
                            "aria-disabled": props.disabled || undefined,
                            tabindex: props.modelValue.destination === destination ? 0 : -1,
                            "aria-controls": destination === 'shared' ? _unref(sharedBlockId) : undefined,
                            class: _normalizeClass([
                                cardBase,
                                props.modelValue.destination === destination ? cardSelected : cardIdle,
                                props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            ]),
                            onClick: ($event) => (select(destination))
                        }, [
                            _createElementVNode("span", _hoisted_5, [
                                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)(`advancedFilterLibrary.save.${destination}Title`)), 1 /* TEXT */),
                                _createElementVNode("span", null, _toDisplayString(_unref(t)(`advancedFilterLibrary.save.${destination}Helper`)), 1 /* TEXT */)
                            ])
                        ], 10 /* CLASS, PROPS */, _hoisted_4);
                    }), 64 /* STABLE_FRAGMENT */))
                ], 40 /* PROPS, NEED_HYDRATION */, _hoisted_3),
                _createElementVNode("p", _hoisted_7, _toDisplayString(announcement.value), 1 /* TEXT */),
                (isShared.value)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        id: _unref(sharedBlockId),
                        class: "flex flex-col gap-4"
                    }, [
                        (_unref(unavailable))
                            ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(t)("advancedFilterLibrary.access.directoryUnavailable")), 1 /* TEXT */))
                            : (_unref(rateLimited))
                                ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(t)("advancedFilterLibrary.access.directoryRateLimited")), 1 /* TEXT */))
                                : (_unref(failed))
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_unref(t)("advancedFilterLibrary.access.directoryError")), 1 /* TEXT */))
                                    : _createCommentVNode("v-if", true),
                        _createVNode(_unref(AccessSettings), {
                            "model-value": props.modelValue.access,
                            "user-options": _unref(options),
                            "known-users": knownUsersForSettings.value,
                            "users-loading": _unref(loading),
                            disabled: props.disabled,
                            labels: labels.value,
                            "show-team": false,
                            "onUpdate:modelValue": onAccessChange,
                            onSearch: _unref(search)
                        }, null, 8 /* PROPS */, ["model-value", "user-options", "known-users", "users-loading", "disabled", "labels", "onSearch"])
                    ], 8 /* PROPS */, _hoisted_8))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
